import { useMutation, useQuery } from "@tanstack/react-query";
import { Edit2, Info, Link2, Loader2, Plus, Search, Trash2, X } from "lucide-react";
import type React from "react";
import { useMemo, useState } from "react";
import { createEdge, createNode, deleteEdge, listNodes } from "@/api/knowledgeGraphApi";
import { getNodeColorSwatchClass } from "@/features/graph/utils/colorSwatch";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useRefreshGraph } from "@/hooks/useRefreshGraph";
import type { VisualNode } from "@/types/knowledgeGraph";
import { searchKnowledgeElements } from "@/utils/knowledgeElementSearch";

export interface EditingInformationPanelProps {
	selectedNode: VisualNode | null;
	visible: boolean;
	onToggle: () => void;
	onDeleteNode?: (nodeId: string) => void;
	graphId?: string | null;
	onGraphUpdate?: () => void;
	graphNodes?: VisualNode[];
	edges?: Array<{
		id: string;
		predicateId: string;
		source: string;
		target: string;
		label: string;
	}>;
}

/**
 * Editing Information Panel component
 * Displays information about the selected node in editing mode
 * Allows deletion of nodes
 */
export const EditingInformationPanel: React.FC<EditingInformationPanelProps> = ({
	selectedNode,
	visible,
	onToggle,
	onDeleteNode,
	graphId,
	onGraphUpdate,
	graphNodes = [],
	edges = [],
}) => {
	const safeEdges = edges ?? [];
	const safeGraphNodes = graphNodes ?? [];

	// Search state
	const [searchQuery, setSearchQuery] = useState("");
	const [showSearch, setShowSearch] = useState(false);

	// Node creation state
	const [showCreateNode, setShowCreateNode] = useState(false);
	const [newNodeName, setNewNodeName] = useState("");
	const [newNodeType, setNewNodeType] = useState("Entity");

	// Relation creation state
	const [showCreateRelation, setShowCreateRelation] = useState(false);
	const [relationTargetSearch, setRelationTargetSearch] = useState("");
	const [selectedTarget, setSelectedTarget] = useState<{ id: string; name: string } | null>(null);
	const [newRelationType, setNewRelationType] = useState("");
	const [isCreatingRelation, setIsCreatingRelation] = useState(false);
	const [isDeletingRelation, setIsDeletingRelation] = useState(false);

	// Hook for refreshing graph visualization
	const refreshGraph = useRefreshGraph(graphId, onGraphUpdate);

	// Helper: Reset relation form state
	const resetRelationForm = () => {
		setShowCreateRelation(false);
		setRelationTargetSearch("");
		setNewRelationType("");
		setSelectedTarget(null);
	};

	// Get edges connected to the selected node
	const connectedEdges = selectedNode
		? safeEdges.filter((edge) => edge.source === selectedNode.id || edge.target === selectedNode.id)
		: [];

	const debouncedSearchQuery = useDebouncedValue(searchQuery, 250);
	const debouncedRelationTargetSearch = useDebouncedValue(relationTargetSearch, 250);

	const shouldLoadNodes = showSearch || showCreateRelation;

	const { data: graphNodesData, isLoading: isLoadingGraphNodes } = useQuery({
		queryKey: ["graphNodes", graphId],
		queryFn: ({ signal }) => {
			if (!graphId) throw new Error("graphId required");
			return listNodes(graphId, { signal });
		},
		enabled: shouldLoadNodes && !!graphId,
		staleTime: 1000 * 60 * 2,
	});

	const allNodes = graphNodesData ?? [];

	const isSearching =
		showSearch && searchQuery.trim().length > 0 && (isLoadingGraphNodes || debouncedSearchQuery !== searchQuery);

	const isSearchingTarget =
		showCreateRelation &&
		relationTargetSearch.trim().length > 0 &&
		(isLoadingGraphNodes || debouncedRelationTargetSearch !== relationTargetSearch);

	// Add Nodes: nodes in the current graph (excluding those already displayed as VisualNodes)
	const nodeElementsForAdd = useMemo(() => allNodes.filter((n) => n.elementType !== "Predicate"), [allNodes]);

	const searchResults = useMemo(() => {
		if (!showSearch || !graphId) return [];
		return searchKnowledgeElements(nodeElementsForAdd, debouncedSearchQuery, {
			graphId,
			elementType: "nodes",
			includeAllWhenEmpty: true,
		});
	}, [nodeElementsForAdd, debouncedSearchQuery, showSearch, graphId]);

	// Relation Target: nodes already displayed in graph (safeGraphNodes from vis)
	const relationTargetResults = useMemo(() => {
		if (!showCreateRelation || !graphId) return [];
		return searchKnowledgeElements(safeGraphNodes, debouncedRelationTargetSearch, {
			graphId,
			elementType: "nodes",
			includeAllWhenEmpty: false,
		});
	}, [safeGraphNodes, debouncedRelationTargetSearch, showCreateRelation, graphId]);

	// Mutation to create a new node directly in the graph
	const createNodeMutation = useMutation({
		mutationFn: ({ name, elementType }: { name: string; elementType: string }) => {
			if (!graphId) throw new Error("graphId required");
			return createNode(graphId, { name, elementType });
		},
		onSuccess: (newNode) => {
			refreshGraph();
			setShowCreateNode(false);
			setNewNodeName("");
			alert(`Successfully created "${newNode.name}" in the graph`);
		},
		onError: (error) => {
			alert(`Failed to create node: ${error instanceof Error ? error.message : "Unknown error"}`);
		},
	});

	const handleClearSearch = () => {
		setSearchQuery("");
		setShowSearch(false);
	};

	const handleToggleSearch = () => {
		setShowSearch(!showSearch);
		if (showSearch) {
			setShowCreateRelation(false);
		}
	};

	const handleToggleCreateRelation = () => {
		if (!selectedNode) {
			alert("Please select a source node first");
			return;
		}
		setShowCreateRelation(!showCreateRelation);
		if (showCreateRelation) {
			setShowSearch(false);
		}
	};

	const handleCreateNode = () => {
		if (!newNodeName.trim()) {
			alert("Please enter a node name");
			return;
		}
		createNodeMutation.mutate({ name: newNodeName.trim(), elementType: newNodeType });
	};

	const handleCreateRelation = async (targetElement: { id: string; name: string }) => {
		if (!graphId || !selectedNode || !newRelationType.trim()) {
			alert("Please enter a relation type");
			return;
		}

		setIsCreatingRelation(true);
		try {
			await createEdge(graphId, {
				source: selectedNode.id,
				target: targetElement.id,
				label: newRelationType.trim(),
			});
			refreshGraph();
			alert(
				`Successfully created relation "${newRelationType.trim()}" from "${selectedNode.name}" to "${targetElement.name}"`,
			);
			resetRelationForm();
		} catch (error) {
			alert(`Failed to create relation: ${error instanceof Error ? error.message : "Unknown error"}`);
		} finally {
			setIsCreatingRelation(false);
		}
	};

	const handleDeleteRelation = async (edge: (typeof safeEdges)[0]) => {
		if (!graphId || !selectedNode) return;

		if (
			!window.confirm(`Are you sure you want to delete the relation "${edge.label}"? This action cannot be undone.`)
		) {
			return;
		}

		setIsDeletingRelation(true);
		try {
			await deleteEdge(graphId, edge.id);
			refreshGraph();
			alert(`Successfully deleted relation "${edge.label}"`);
		} catch (error) {
			alert(`Failed to delete relation: ${error instanceof Error ? error.message : "Unknown error"}`);
		} finally {
			setIsDeletingRelation(false);
		}
	};

	if (!visible) {
		return (
			<div className="w-80 surface-alt border-r border-strong flex flex-col h-full text-primary">
				<div className="p-4 border-b border-subtle flex items-center justify-between">
					<h2 className="text-base font-semibold text-primary">Edit Mode</h2>
					<button
						type="button"
						onClick={onToggle}
						className="p-1 soft-hover rounded-lg transition-colors"
						aria-label="Expand editing panel"
					>
						<Info className="w-4 h-4 text-muted" />
					</button>
				</div>
				<div className="flex-1 p-4">
					<p className="text-sm text-subtle">Click on a node to edit or delete it</p>
				</div>
			</div>
		);
	}

	return (
		<div className="w-80 surface-alt border-r border-strong flex flex-col h-full text-primary">
			{/* Header */}
			<div className="p-4 border-b border-subtle surface flex items-center justify-between">
				<div className="flex items-center gap-2">
					<Edit2 className="w-4 h-4 text-primary-600" />
					<h2 className="text-base font-semibold text-primary">Edit Mode</h2>
				</div>
				<button
					type="button"
					onClick={onToggle}
					className="p-1 soft-hover rounded-lg transition-colors"
					aria-label="Collapse editing panel"
				>
					<Info className="w-4 h-4 text-muted" />
				</button>
			</div>

			{/* Instructions */}
			<div className="p-4 border-b border-subtle surface-muted space-y-2">
				<p className="text-xs text-muted">Click on a node in the graph to view its details and delete it if needed.</p>
				{/* Action Buttons */}
				<div className="flex gap-2">
					<button
						type="button"
						onClick={handleToggleSearch}
						className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm"
					>
						<Search className="w-4 h-4" />
						<span>{showSearch ? "Hide Search" : "Add Nodes"}</span>
					</button>
					<button
						type="button"
						onClick={handleToggleCreateRelation}
						disabled={!selectedNode}
						className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
					>
						<Link2 className="w-4 h-4" />
						<span>{showCreateRelation ? "Cancel" : "Add Relation"}</span>
					</button>
				</div>
			</div>

			{/* Search Section */}
			{showSearch && (
				<div className="p-4 border-b border-subtle surface">
					<div className="relative mb-3">
						<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-subtle" />
						<input
							type="text"
							placeholder="Search nodes by name..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							className="w-full pl-10 pr-10 py-2 border border-strong surface text-primary rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
						/>
						{searchQuery && (
							<button
								type="button"
								onClick={handleClearSearch}
								className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 soft-hover rounded transition-colors"
								aria-label="Clear search"
							>
								<X className="w-4 h-4 text-subtle" />
							</button>
						)}
					</div>

					{/* Search Results - show graph nodes; filter when user types */}
					<div className="max-h-64 overflow-y-auto border border-subtle rounded-lg">
						{isSearching ? (
							<div className="p-4 text-center">
								<Loader2 className="w-5 h-5 animate-spin text-primary-600 mx-auto mb-2" />
								<p className="text-xs text-subtle">Searching...</p>
							</div>
						) : searchResults && searchResults.length > 0 ? (
							<div className="divide-y divide-[var(--border)]">
								{searchResults.slice(0, 50).map((element) => (
									<div key={element.id} className="p-3 soft-hover transition-colors">
										<div className="flex items-start justify-between gap-2">
											<div className="flex-1 min-w-0">
												<div className="text-sm font-medium text-primary truncate">{element.name}</div>
												<div className="text-xs text-subtle mt-0.5">{element.elementType}</div>
											</div>
											<span className="p-1.5 text-subtle flex-shrink-0">
												<Plus className="w-4 h-4 opacity-30" />
											</span>
										</div>
									</div>
								))}
								{searchResults.length > 50 && (
									<div className="p-2 text-xs text-subtle text-center">
										Showing first 50 of {searchResults.length} results
									</div>
								)}
							</div>
						) : searchQuery.trim().length > 0 ? (
							<div className="p-4 text-center">
								<p className="text-xs text-subtle mb-3">No nodes found matching "{searchQuery}"</p>
								<button
									type="button"
									onClick={() => {
										setShowCreateNode(true);
										setNewNodeName(searchQuery);
									}}
									className="text-xs text-primary-600 hover:text-primary-700 font-medium"
								>
									Create "{searchQuery}" as new node
								</button>
							</div>
						) : (
							<div className="p-4 text-center">
								<p className="text-xs text-neutral-500">No elements available to add.</p>
							</div>
						)}
					</div>

					{/* Create Node Form */}
					{showCreateNode && (
						<div className="mt-3 p-3 border border-strong rounded-lg surface-muted">
							<div className="mb-2">
								<label htmlFor="create-node-name" className="block text-xs font-medium text-muted mb-1">
									Node Name
								</label>
								<input
									id="create-node-name"
									type="text"
									value={newNodeName}
									onChange={(e) => setNewNodeName(e.target.value)}
									placeholder="Enter node name..."
									className="w-full px-3 py-2 border border-strong surface text-primary rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
								/>
							</div>
							<div className="mb-3">
								<label htmlFor="create-node-type" className="block text-xs font-medium text-muted mb-1">
									Node Type
								</label>
								<select
									id="create-node-type"
									value={newNodeType}
									onChange={(e) => setNewNodeType(e.target.value)}
									className="w-full px-3 py-2 border border-strong surface text-primary rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
								>
									<option value="Entity">Entity</option>
									<option value="Predicate">Predicate</option>
								</select>
							</div>
							<div className="flex gap-2">
								<button
									type="button"
									onClick={handleCreateNode}
									disabled={createNodeMutation.isPending}
									className="flex-1 px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
								>
									{createNodeMutation.isPending ? (
										<>
											<Loader2 className="w-4 h-4 animate-spin inline mr-2" />
											Creating...
										</>
									) : (
										"Create & Add"
									)}
								</button>
								<button
									type="button"
									onClick={() => {
										setShowCreateNode(false);
										setNewNodeName("");
									}}
									className="px-3 py-2 btn btn-secondary text-sm font-medium"
								>
									Cancel
								</button>
							</div>
						</div>
					)}
				</div>
			)}

			{/* Create Relation Section */}
			{showCreateRelation && selectedNode && (
				<div className="p-4 border-b border-subtle surface">
					<div className="mb-3">
						<p className="text-xs font-medium text-muted mb-1">
							From: <span className="font-semibold">{selectedNode.name}</span>
						</p>
					</div>

					{!selectedTarget ? (
						/* Target Node Search */
						<div>
							<label htmlFor="relation-target-search" className="block text-xs font-medium text-muted mb-1">
								Select Target Node
							</label>
							<div className="relative mb-2">
								<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-subtle" />
								<input
									id="relation-target-search"
									type="text"
									placeholder="Search target node..."
									value={relationTargetSearch}
									onChange={(e) => setRelationTargetSearch(e.target.value)}
									className="w-full pl-10 pr-10 py-2 border border-strong surface text-primary rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
								/>
								{relationTargetSearch && (
									<button
										type="button"
										onClick={() => setRelationTargetSearch("")}
										className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 soft-hover rounded transition-colors"
									>
										<X className="w-4 h-4 text-subtle" />
									</button>
								)}
							</div>

							{relationTargetSearch.trim().length > 0 && (
								<div className="max-h-32 overflow-y-auto border border-subtle rounded-lg">
									{isSearchingTarget ? (
										<div className="p-3 text-center">
											<Loader2 className="w-4 h-4 animate-spin text-primary-600 mx-auto" />
										</div>
									) : relationTargetResults && relationTargetResults.length > 0 ? (
										<div className="divide-y divide-[var(--border)]">
											{relationTargetResults.slice(0, 5).map((element) => (
												<button
													key={element.id}
													type="button"
													onClick={() => setSelectedTarget(element)}
													className="w-full text-left p-2 soft-hover transition-colors"
												>
													<div className="text-sm font-medium text-primary">{element.name}</div>
													<div className="text-xs text-subtle">{element.elementType}</div>
												</button>
											))}
										</div>
									) : (
										<div className="p-3 text-center text-xs text-subtle">No nodes found</div>
									)}
								</div>
							)}
						</div>
					) : (
						/* Relation Type Entry */
						<div>
							<div className="mb-2 flex items-center justify-between">
								<div>
									<p className="text-xs font-medium text-muted">
										To: <span className="font-semibold">{selectedTarget.name}</span>
									</p>
								</div>
								<button
									type="button"
									onClick={() => {
										setSelectedTarget(null);
										setNewRelationType("");
									}}
									className="text-xs text-subtle hover-text"
								>
									Change target
								</button>
							</div>

							<label htmlFor="relation-type-input" className="block text-xs font-medium text-muted mb-1">
								Relation Label
							</label>
							<div className="flex gap-2">
								<input
									id="relation-type-input"
									type="text"
									placeholder="e.g. related_to, causes..."
									value={newRelationType}
									onChange={(e) => setNewRelationType(e.target.value)}
									className="flex-1 px-3 py-2 border border-strong surface text-primary rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
								/>
								<button
									type="button"
									onClick={() => handleCreateRelation(selectedTarget)}
									disabled={isCreatingRelation || !newRelationType.trim()}
									className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
								>
									{isCreatingRelation ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create"}
								</button>
							</div>
						</div>
					)}
				</div>
			)}

			{/* Node Information */}
			<div className="flex-1 overflow-y-auto">
				{!selectedNode ? (
					<div className="p-4 text-center">
						<div className="text-sm text-subtle py-8">
							<Edit2 className="w-8 h-8 mx-auto mb-2 text-subtle" />
							<p>Select a node to view details</p>
						</div>
					</div>
				) : (
					<div className="p-4 space-y-4">
						{/* Node Details - Reusing InformationPanel design */}
						<div className="surface border border-subtle rounded-lg shadow-sm p-4">
							<div className="flex items-center justify-between mb-4">
								<div className="flex items-center gap-2">
									<Info className="w-5 h-5 text-muted" />
									<h3 className="text-sm font-semibold text-primary">Node Details</h3>
								</div>
							</div>

							<div className="space-y-3">
								<div>
									<div className="text-xs font-medium text-subtle mb-1">Name</div>
									<div className="text-sm font-semibold text-primary">{selectedNode.name}</div>
								</div>

								<div>
									<div className="text-xs font-medium text-subtle mb-1">ID</div>
									<div className="text-sm text-muted font-mono break-all">{selectedNode.id}</div>
								</div>

								<div>
									<div className="text-xs font-medium text-subtle mb-1">Graph Element ID</div>
									<div className="text-sm text-muted font-mono break-all">{selectedNode.graphElementId}</div>
								</div>

								<div>
									<div className="text-xs font-medium text-subtle mb-1">Element Type</div>
									<div className="text-sm text-muted">{selectedNode.elementType}</div>
								</div>

								{selectedNode.typeId && (
									<div>
										<div className="text-xs font-medium text-subtle mb-1">Type ID</div>
										<div className="text-sm text-muted font-mono break-all">{selectedNode.typeId}</div>
									</div>
								)}

								{selectedNode.color && (
									<div>
										<div className="text-xs font-medium text-subtle mb-1">Color</div>
										<div className="flex items-center gap-2">
											<div className={getNodeColorSwatchClass(selectedNode.color)} />
											<div className="text-sm text-muted font-mono">{selectedNode.color}</div>
										</div>
									</div>
								)}

								{selectedNode.group !== undefined && (
									<div>
										<div className="text-xs font-medium text-subtle mb-1">Community</div>
										<div className="text-sm text-muted">{selectedNode.group}</div>
									</div>
								)}

								{selectedNode.radius !== undefined && (
									<div>
										<div className="text-xs font-medium text-subtle mb-1">Radius</div>
										<div className="text-sm text-muted">{selectedNode.radius.toFixed(2)}</div>
									</div>
								)}

								{selectedNode.metadata && Object.keys(selectedNode.metadata).length > 0 && (
									<div>
										<div className="text-xs font-medium text-subtle mb-1">Metadata</div>
										<div className="text-sm text-muted surface-muted p-2 rounded font-mono max-h-32 overflow-y-auto">
											<pre className="whitespace-pre-wrap text-xs">
												{JSON.stringify(selectedNode.metadata, null, 2)}
											</pre>
										</div>
									</div>
								)}
							</div>
						</div>

						{/* Relations Section */}
						{connectedEdges.length > 0 && (
							<div className="surface border border-subtle rounded-lg shadow-sm p-4">
								<div className="flex items-center justify-between mb-4">
									<div className="flex items-center gap-2">
										<Link2 className="w-5 h-5 text-muted" />
										<h3 className="text-sm font-semibold text-primary">Relations ({connectedEdges.length})</h3>
									</div>
								</div>

								<div className="space-y-2 max-h-64 overflow-y-auto">
									{connectedEdges.map((edge) => {
										const sourceId = edge.source;
										const targetId = edge.target;
										const isOutgoing = sourceId === selectedNode?.id;
										const otherNodeId = isOutgoing ? targetId : sourceId;

										return (
											<div
												key={edge.id}
												className="flex items-center justify-between p-2 surface-muted rounded border border-strong soft-hover transition-colors"
											>
												<div className="flex-1 min-w-0">
													<div className="text-xs text-subtle mb-0.5">{isOutgoing ? "To" : "From"}</div>
													<div className="text-sm font-medium text-primary truncate">{edge.label}</div>
													<div className="text-xs text-subtle truncate">Node ID: {otherNodeId.slice(0, 8)}...</div>
												</div>
												<button
													type="button"
													onClick={() => handleDeleteRelation(edge)}
													disabled={isDeletingRelation}
													className="p-1.5 hover:bg-red-50 text-red-600 rounded transition-colors flex-shrink-0 disabled:opacity-50"
													aria-label={`Delete relation ${edge.label}`}
												>
													<Trash2 className="w-4 h-4" />
												</button>
											</div>
										);
									})}
								</div>
							</div>
						)}

						{/* Delete Button */}
						{onDeleteNode && (
							<button
								type="button"
								onClick={() => {
									if (
										window.confirm(
											`Are you sure you want to delete "${selectedNode.name}"? This action cannot be undone.`,
										)
									) {
										onDeleteNode(selectedNode.id);
									}
								}}
								className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-sm"
							>
								<Trash2 className="w-4 h-4" />
								<span>Delete Node</span>
							</button>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

export default EditingInformationPanel;
